/**
 * API Error Emitter
 * A simple event bus for API errors. The ApiService publishes errors here,
 * and the ErrorContext subscribes to them.
 * This decouples the Axios singleton from the React context.
 */

export type ApiErrorSeverity = 'error' | 'warning' | 'info';
export type ApiErrorType = 'network' | 'auth' | 'forbidden' | 'validation' | 'server' | 'unknown';

export interface ApiErrorEvent {
  message: string;
  type: ApiErrorType;
  severity: ApiErrorSeverity;
  status?: number;
  details?: string;
}

type ErrorListener = (event: ApiErrorEvent) => void;

class ApiErrorEmitter {
  private listeners: ErrorListener[] = [];

  /** Subscribe to API error events. Returns an unsubscribe function. */
  subscribe(listener: ErrorListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /** Emit an error event to all subscribers. */
  emit(event: ApiErrorEvent): void {
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch {
        // Prevent listener errors from breaking the chain
      }
    });
  }
}

const apiErrorEmitter = new ApiErrorEmitter();
export default apiErrorEmitter;
